//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useState } from 'react';
import {
  Collapse, Divider, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExtensionIcon from '@mui/icons-material/Extension';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { drawerRowSx } from './drawerRowSx';
import { type PluginSurfaceRow } from '@/components/Header/pluginSurfaces';

interface PluginsDrawerSectionProps {
  rows: PluginSurfaceRow[];
  onSelect: (row: PluginSurfaceRow) => void;
}

const PluginsDrawerSection = ({
  rows, onSelect
}: PluginsDrawerSectionProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  if (rows.length === 0) return null;

  const rowSx = drawerRowSx(theme);

  return (
    <>
      <Divider
        component="li"
        sx={{ borderColor: theme.palette.border.default, my: 1, mx: 2 }}
      />
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => setExpanded((prev) => !prev)}
          aria-label="Plugins"
          aria-expanded={expanded}
          sx={rowSx}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ExtensionIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase'
                }}
              >
                Plugins
              </Typography>
            }
          />
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemButton>
      </ListItem>
      <Collapse component="li" in={expanded} timeout="auto" unmountOnExit>
        <List disablePadding>
          {rows.map((row) => (
            <ListItem
              key={`plugin-${row.plugin.name}-${row.key}`}
              disablePadding
            >
              <ListItemButton
                onClick={() => onSelect(row)}
                aria-label={row.surface.label}
                sx={{ ...rowSx, pl: 4 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ExtensionIcon />
                </ListItemIcon>
                <ListItemText primary={row.surface.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default PluginsDrawerSection;
